<template>
  <BModal
    :id="id"
    v-model="visible"
    :no-close-on-backdrop="computedNoCloseOnBackdrop"
    :no-close-on-esc="computedNoCloseOnEsc"
    hide-header
    hide-footer
    centered
    body-class="!p-[24px] relative"
    dialog-class="modal-totp-dialog"
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <!-- Close Button -->
    <button
      type="button"
      class="absolute top-[16px] right-[16px] z-10 text-[#737373] hover:text-[#0A0A0A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors p-[4px] cursor-pointer bg-transparent border-0 focus:outline-none"
      aria-label="Close"
      :disabled="loading"
      @click="hide"
    >
      <feather-icon
        icon="XIcon"
        size="20"
      />
    </button>

    <!-- View 1: OTP Input Flow -->
    <div
      v-if="!isBlocked"
      class="totp-container"
    >
      <!-- Title & Subtitle -->
      <div class="text-center">
        <h4 class="font-medium text-[#0A0A0A] text-[18px] md:text-2xl text-[#111827] mb-[4px]">
          {{ title }}
        </h4>
        <p class="text-[14px] text-[#737373] mb-0">
          {{ subtitle }}
        </p>
      </div>

      <!-- 6-digit OTP input boxes -->
      <div
        class="flex justify-center items-center gap-[16px] my-[12px]"
        @paste.prevent="handlePaste"
      >
        <div
          v-for="(digit, index) in digits"
          :key="`totp-box-${index}`"
          :class="[
            'totp-input-box',
            { 'is-active': activeIndex === index },
            { 'has-value': digits[index] !== '' },
            { 'is-disabled': isInputDisabled(index) },
          ]"
          @click="focusInput(index)"
        >
          <input
            ref="otpInputs"
            v-model="digits[index]"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="1"
            :disabled="loading"
            class="totp-input-field"
            @focus="onFocus(index)"
            @blur="onBlur(index)"
            @input="handleInput($event, index)"
            @keydown="handleKeydown($event, index)"
          >
          <span
            v-if="!digits[index]"
            class="totp-bottom-bar"
          />
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="errorMessage"
        class="text-center text-[#F95031] text-[12px] mb-[12px] animate__animated animate__fadeIn"
      >
        {{ errorMessage }}
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col gap-[12px]">
        <!-- Submit Button -->
        <button
          type="button"
          :disabled="isSubmitDisabled"
          :class="[
            'totp-btn-kirim w-full text-[#FFFFFF] shadow-[0_1px_2px_0px_#0000001A] font-medium text-[14px] px-[16px] py-[8px] rounded-[10px] transition duration-200 flex items-center justify-center gap-[8px]',
            isSubmitDisabled
              ? 'bg-[#C2C2C2] cursor-not-allowed'
              : 'bg-[#F95E16] hover:bg-[#E04106] cursor-pointer'
          ]"
          @click="submitOtp"
        >
          <BSpinner
            v-if="loading"
            small
            variant="light"
          />
          <span>Kirim</span>
        </button>
      </div>
    </div>

    <!-- View 2: Blocked / Restricted Flow -->
    <div
      v-else
      class="totp-container text-center"
    >
      <!-- Warning Illustration -->
      <div class="flex justify-center mb-[24px]">
        <img
          :src="warningIconUrl"
          alt="Warning"
          class="size-[100px] object-contain"
        >
      </div>

      <!-- Title & Subtitle -->
      <h4 class="font-medium text-[18px] text-[#0A0A0A] mb-[4px]">
        {{ currentBlockedTitle }}
      </h4>
      <p class="text-[14px] text-[#737373] mx-auto mb-[24px] leading-[20px]">
        {{ currentBlockedSubtitle }}
      </p>

      <!-- Dismiss Button -->
      <button
        type="button"
        class="w-full bg-[#F95E16] hover:bg-[#E04106] text-white font-medium text-[14px] py-[8px] px-[16px] rounded-[10px] shadow-[0px_1px_2px_0px_#0000001A] transition duration-200 cursor-pointer"
        @click="handleUnderstand"
      >
        {{ blockedButtonText }}
      </button>
    </div>
  </BModal>
</template>

<script>
import { BModal, BSpinner } from 'bootstrap-vue'

export default {
  name: 'ModalTOTP',
  components: {
    BModal,
    BSpinner,
  },
  props: {
    id: {
      type: String,
      default: 'modal-totp',
    },
    noCloseOnBackdrop: {
      type: Boolean,
      default: false,
    },
    noCloseOnEsc: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visible: false,
      digits: ['', '', '', '', '', ''],
      activeIndex: 0,
      loading: false,
      errorMessage: '',
      title: 'Verifikasi Kode OTP',
      subtitle: 'Masukkan 6 digit angka di aplikasi authenticator',
      maxUnlockedIndex: 5,

      // Blocked / restricted state
      isBlocked: false,
      blockedVariant: 'failed', // 'failed' | 'restricted'
      blockedTitleCustom: '',
      blockedSubtitleCustom: '',
      blockedButtonText: 'Mengerti',

      // Icon fallback
      warningIconUrl: 'https://storage.googleapis.com/komerce/core/icon-popup-warning.png',

      // Callback references
      onSubmitCallback: null,
      onUnderstandCallback: null,
    }
  },
  computed: {
    otpCode() {
      return this.digits.join('')
    },
    isSubmitDisabled() {
      return this.otpCode.length < 6 || this.loading
    },
    computedNoCloseOnBackdrop() {
      return this.loading || this.noCloseOnBackdrop
    },
    computedNoCloseOnEsc() {
      return this.loading || this.noCloseOnEsc
    },
    currentBlockedTitle() {
      if (this.blockedTitleCustom) return this.blockedTitleCustom
      if (this.blockedVariant === 'restricted') return 'Akun Kamu Dibatasi!'
      return 'Verifikasi kode otp gagal'
    },
    currentBlockedSubtitle() {
      if (this.blockedSubtitleCustom) return this.blockedSubtitleCustom
      return 'Kode OTP (2FA) salah 3 kali. Silahkan coba lagi setelah 24 jam kedepan'
    },
  },
  methods: {
    // -------------------------------------------------------------
    // Public Modal Control API
    // -------------------------------------------------------------
    show(options = {}) {
      this.reset()

      if (options.title) this.title = options.title
      if (options.subtitle) this.subtitle = options.subtitle
      if (options.onSubmit) this.onSubmitCallback = options.onSubmit
      if (options.onUnderstand) this.onUnderstandCallback = options.onUnderstand

      this.visible = true
      this.focusFirstInput()
    },

    hide() {
      this.visible = false
    },

    setLoading(value = true) {
      this.loading = Boolean(value)
    },

    /**
     * Set error message on the modal.
     * Accepts either a message string or an options object { message }.
     */
    setError(messageOrOptions) {
      this.loading = false

      let message = ''
      if (typeof messageOrOptions === 'object' && messageOrOptions !== null) {
        message = messageOrOptions.message || ''
      } else {
        message = messageOrOptions || ''
      }

      this.errorMessage = message || 'Kode OTP Salah'

      // Keep current digits; restrict typing sequentially from the first field
      this.maxUnlockedIndex = 0

      this.$nextTick(() => {
        this.focusInput(0)
      })
    },

    /**
     * Transition to blocked or restricted state view.
     * @param {string|object} options - Variant ('failed' | 'restricted') or config object.
     */
    setBlocked(options = 'failed') {
      this.loading = false
      this.isBlocked = true

      if (typeof options === 'string') {
        this.blockedVariant = options
        this.blockedTitleCustom = ''
        this.blockedSubtitleCustom = ''
      } else if (typeof options === 'object' && options !== null) {
        this.blockedVariant = options.variant || 'failed'
        if (options.title) this.blockedTitleCustom = options.title
        if (options.subtitle) this.blockedSubtitleCustom = options.subtitle
        if (options.buttonText) this.blockedButtonText = options.buttonText
      }

      this.$emit('blocked', {
        variant: this.blockedVariant,
        title: this.currentBlockedTitle,
        subtitle: this.currentBlockedSubtitle,
      })
    },

    reset() {
      this.digits = ['', '', '', '', '', '']
      this.errorMessage = ''
      this.maxUnlockedIndex = 5
      this.loading = false
      this.isBlocked = false
      this.blockedVariant = 'failed'
      this.blockedTitleCustom = ''
      this.blockedSubtitleCustom = ''
    },

    // -------------------------------------------------------------
    // Input Handling & Keyboard Navigation
    // -------------------------------------------------------------
    getInputElements() {
      if (this.$refs.otpInputs && Array.isArray(this.$refs.otpInputs) && this.$refs.otpInputs.length) {
        return this.$refs.otpInputs
      }
      if (this.$el) {
        const nodes = this.$el.querySelectorAll('.totp-input-field')
        if (nodes && nodes.length) {
          return Array.from(nodes)
        }
      }
      return []
    },

    isInputDisabled() {
      return this.loading
    },

    focusInput(index) {
      if (this.isInputDisabled()) {
        return
      }

      const executeFocus = () => {
        const inputs = this.getInputElements()
        if (inputs && inputs[index]) {
          inputs[index].focus()
          if (typeof inputs[index].select === 'function' && (index < 5 || !this.digits[index])) {
            inputs[index].select()
          }
          this.activeIndex = index
        }
      }

      executeFocus()
      this.$nextTick(() => {
        executeFocus()
      })
    },

    focusFirstInput() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.isBlocked) {
            this.focusInput(0)
          }
        }, 50)
      })
    },

    onFocus(index) {
      if (this.isInputDisabled(index)) {
        this.focusInput(index)
        return
      }
      this.activeIndex = index
    },

    onBlur(index) {
      if (this.activeIndex === index) {
        this.activeIndex = -1
      }
    },

    handleInput(event, index) {
      const val = event.target.value

      // Ignore input when the last box is already filled
      if (index === 5 && this.digits[index] !== '' && val !== this.digits[index]) {
        this.$forceUpdate()
        return
      }

      // Handle multi-character paste or autofill
      if (val.length > 1) {
        const cleaned = val.replace(/\D/g, '')
        if (cleaned.length > 0) {
          this.fillDigitsFromText(cleaned, index)
        } else {
          this.$set(this.digits, index, '')
        }
        return
      }

      // Single digit input
      const cleanedChar = val.replace(/\D/g, '')
      this.$set(this.digits, index, cleanedChar)

      // Move focus to next input if digit entered
      if (cleanedChar && index < 5) {
        if (this.maxUnlockedIndex < index + 1) {
          this.maxUnlockedIndex = index + 1
        }
        this.focusInput(index + 1)
      }
    },

    handleKeydown(event, index) {
      const { key } = event

      if (key === 'Backspace') {
        const isLimited = Boolean(this.errorMessage)

        if (!this.digits[index] && index > 0) {
          event.preventDefault()
          this.$set(this.digits, index - 1, '')
          if (isLimited) {
            this.maxUnlockedIndex = Math.max(0, index - 1)
          }
          this.focusInput(index - 1)
        } else {
          this.$set(this.digits, index, '')
          if (isLimited) {
            this.maxUnlockedIndex = Math.max(0, index)
          }
        }
      } else if (key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        this.focusInput(index - 1)
      } else if (key === 'ArrowRight' && index < 5) {
        event.preventDefault()
        this.focusInput(index + 1)
      } else if (key === 'Enter') {
        if (!this.isSubmitDisabled) {
          this.submitOtp()
        }
      } else if (/^[0-9]$/.test(key)) {
        if (this.isInputDisabled(index)) {
          event.preventDefault()
          return
        }

        // Prevent overwriting when the last box is already filled
        if (index === 5 && this.digits[5] !== '') {
          event.preventDefault()
          return
        }

        // Overwrite current box and advance focus to next box
        if (this.digits[index] && index < 5) {
          event.preventDefault()
          this.$set(this.digits, index, key)
          if (this.maxUnlockedIndex < index + 1) {
            this.maxUnlockedIndex = index + 1
          }
          this.focusInput(index + 1)
        }
      }
    },

    handlePaste(event) {
      const pasteText = (event.clipboardData || window.clipboardData).getData('text')
      const cleaned = pasteText.replace(/\D/g, '')
      if (cleaned.length > 0) {
        this.fillDigitsFromText(cleaned, 0)
      }
    },

    fillDigitsFromText(text, startIndex = 0) {
      const chars = text.slice(0, 6).split('')
      let lastFilled = startIndex

      chars.forEach((char, i) => {
        const targetIndex = startIndex + i
        if (targetIndex < 6) {
          this.$set(this.digits, targetIndex, char)
          lastFilled = targetIndex
        }
      })

      this.maxUnlockedIndex = 5
      const nextFocus = lastFilled < 5 ? lastFilled + 1 : 5
      this.focusInput(nextFocus)
    },

    getControlHelpers() {
      return {
        setLoading: this.setLoading,
        setError: this.setError,
        setBlocked: this.setBlocked,
        hide: this.hide,
        close: this.hide,
        reset: this.reset,
        focusInput: this.focusInput,
      }
    },

    // -------------------------------------------------------------
    // Actions & Countdown
    // -------------------------------------------------------------
    async submitOtp() {
      if (this.isSubmitDisabled) return

      const code = this.otpCode
      const helpers = this.getControlHelpers()

      this.$emit('submit', code, helpers)

      if (typeof this.onSubmitCallback === 'function') {
        try {
          const res = this.onSubmitCallback(code, helpers)
          if (res && typeof res.then === 'function') {
            this.setLoading(true)
            await res
          }
        } catch (err) {
          this.setLoading(false)
        }
      }
    },

    handleUnderstand() {
      const helpers = this.getControlHelpers()
      this.$emit('understand', helpers)
      if (typeof this.onUnderstandCallback === 'function') {
        this.onUnderstandCallback(helpers)
      }
      this.hide()
    },

    onModalShown() {
      this.$emit('shown')
      this.focusFirstInput()
    },

    onModalHidden() {
      this.$emit('hidden')
    },
  },
}
</script>

<style lang="scss">
.modal-totp-dialog {
  max-width: 440px !important;

  .modal-content {
    border-radius: 16px !important;
    border: none !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12) !important;
    overflow: hidden;
  }
}

.totp-input-box {
  position: relative;
  width: 49px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  transition: all 0.15s ease-in-out;
  cursor: text;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  color: #737373;

  &:hover {
    border-color: #9CA3AF;
  }

  &.is-active {
    border: 2px solid #71717A !important;
    box-shadow: 0 0 0 4.5px #E4E4E7 !important;
    color: #0A0A0A;
  }

  &.is-disabled {
    cursor: not-allowed;

    &:hover {
      border-color: #E5E5E5;
    }
  }
}

.totp-input-field {
  width: 100%;
  height: 100%;
  text-align: center;
  font-size: 16px;
  background: transparent;
  border: none;
  outline: none;
  caret-color: #111827;
  padding: 0;
  box-shadow: 0 1px 2px 0px #0000001A;

  &:disabled,
  &[readonly] {
    background: transparent;
    cursor: not-allowed;
  }
}

.totp-bottom-bar {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 10px;
  height: 1.5px;
  background-color: #0A0A0A;
  border-radius: 1px;
  pointer-events: none;
}

@media (max-width: 400px) {
  .totp-input-box {
    width: 42px;
    height: 46px;
    border-radius: 10px;
  }
  .totp-input-field {
    font-size: 18px;
  }
  .totp-bottom-bar {
    bottom: 11px;
    width: 11px;
    height: 2px;
  }
}
</style>
